import { merge } from "lodash";
import React, { ComponentProps } from "react";

import { gql, parseExam } from "@examspark/api";
import {
  ActivityEditorConfig,
  ActivityEditorModal,
  useAppContext,
} from "@examspark/common-app";
import { Classroom, Replace, SaveResult, Unit } from "@examspark/types";

import { useMutationAddExamToClassroomBonfire } from "../../hooks/useQueries";
import { activityEditorConfig } from "../workspace/WorkspaceAssessments";

// this component can provide its own defaults for the editorConfig prop
type ActivityEditorModalPickedProps = Replace<
  ComponentProps<typeof ActivityEditorModal>,
  { editorConfig?: ActivityEditorConfig }
>;

type Props = ActivityEditorModalPickedProps & {
  classroom: Classroom | undefined;
  unit: Unit | undefined;
};

const ClassroomActivityEditorModal = ({ classroom, unit, ...props }: Props) => {
  const [state] = useAppContext();
  const addToClassroom = useMutationAddExamToClassroomBonfire();

  unit = unit ?? classroom?.unit ?? undefined;

  if (unit?.id == null || classroom?.id == null) {
    return null;
  }

  const config = merge<
    ActivityEditorConfig,
    ActivityEditorConfig,
    ActivityEditorConfig
  >({}, props.editorConfig ?? activityEditorConfig, {
    itemPicker: {
      initialFilter: {
        referencesIdsUnit: [unit.id],
        subject:
          unit.subject ??
          unit.course?.subject ??
          classroom.course?.subject ??
          [],
        grade:
          unit.grade ?? unit.course?.grade ?? classroom.course?.grade ?? [],
      },
    },
  });

  const handleSave = async (result: SaveResult) => {
    if (
      state.app?.id == null ||
      state.appLicense?.appKey == null ||
      state.user?.id == null ||
      classroom == null ||
      unit == null
    ) {
      return;
    }

    const exam = await gql.operations.exam
      .getExam({ id: result.id, sk: "exam#v0" })
      .then(parseExam);

    if (exam == null) {
      return;
    }

    await addToClassroom.mutateAsync({
      exam,
      classroom: classroom,
      unit: unit,
      name: exam.name ?? "Untitled",
    });

    await props.onSave?.(result);
  };

  return (
    <ActivityEditorModal
      {...props}
      editorConfig={config}
      onBeforeSave={({ data, type }) => {
        if (type === "create") {
          data.isUnlisted = true;
        }
      }}
      onSave={handleSave}
    />
  );
};

export default ClassroomActivityEditorModal;
